import { CatmullRomCurve3, TubeGeometry, Vector3, type BufferGeometry } from 'three'
import type { Ponto2 } from '../componentes/contornos2d'
import { aneisDePega, torno } from '../componentes/malhasProcedurais'
import type { NivelQualidade } from '../ambiente/qualidadeGrafica'

/** Comprimento da mola em repouso: a malha é feita nele e estica por escala. */
export const COMPRIMENTO_MOLA_REPOUSO = 0.12
const ESPIRAS = 22
const RAIO_MOLA = 0.0068
const RAIO_ARAME = 0.0013

const DETALHE: Record<NivelQualidade, { torno: number; amostrasPorEspira: number }> = {
  alta: { torno: 28, amostrasPorEspira: 14 },
  media: { torno: 20, amostrasPorEspira: 10 },
  baixa: { torno: 12, amostrasPorEspira: 6 },
}

/**
 * Mola helicoidal de verdade, um arame só, no lugar dos 22 anéis soltos de
 * antes (22 malhas viravam 1). Feita no comprimento de repouso e esticada por
 * escala em x: no curso máximo ela estica 46%, e o arame fica levemente oval
 * nesse eixo — imperceptível a esse tamanho, e evita recriar a geometria a
 * cada quadro da animação.
 */
function mola(amostrasPorEspira: number) {
  const pontos: Vector3[] = []
  const total = ESPIRAS * amostrasPorEspira
  for (let i = 0; i <= total; i++) {
    const t = i / total
    const a = t * ESPIRAS * Math.PI * 2
    pontos.push(new Vector3(t * COMPRIMENTO_MOLA_REPOUSO, RAIO_MOLA * Math.cos(a), RAIO_MOLA * Math.sin(a)))
  }
  return new TubeGeometry(new CatmullRomCurve3(pontos), total, RAIO_ARAME, 6, false)
}

/** Ponta: carbeto, corpo preto de apoio com sulco anatômico e colar que recebe a mola. */
const PERFIL_PONTA: Ponto2[] = [
  [0.11, 0],
  [0.11, 0.0085],
  [0.118, 0.0085],
  [0.118, 0.0075],
  [0.137, 0.0072],
  [0.138, 0.009],
  [0.142, 0.009],
  [0.143, 0.007],
  [0.152, 0.0062],
  [0.153, 0.0035],
  [0.166, 0.0003],
  [0.1665, 0],
]

/**
 * Empunhadura de tração laranja: corpo levemente cônico com anéis de pega na
 * traseira e a boca que abraça a mola.
 */
function perfilEmpunhadura(): Ponto2[] {
  return [
    [-0.132, 0],
    [-0.132, 0.009],
    [-0.13, 0.0112],
    ...aneisDePega(-0.128, -0.1, 0.0112, 0.0009, 4),
    [-0.098, 0.0108],
    [-0.02, 0.0084],
    [-0.016, 0.0078],
    [-0.016, 0.0055],
    [-0.014, 0.0045],
    [-0.014, 0],
  ]
}

export interface MalhasPuncao {
  readonly mola: BufferGeometry
  readonly ponta: BufferGeometry
  readonly empunhadura: BufferGeometry
}

const CACHE = new Map<NivelQualidade, MalhasPuncao>()

export function malhasPuncao(nivel: NivelQualidade): MalhasPuncao {
  const existente = CACHE.get(nivel)
  if (existente) return existente
  const d = DETALHE[nivel]
  const malhas: MalhasPuncao = {
    mola: mola(d.amostrasPorEspira),
    ponta: torno(PERFIL_PONTA, d.torno),
    empunhadura: torno(perfilEmpunhadura(), d.torno),
  }
  CACHE.set(nivel, malhas)
  return malhas
}
